/* eslint no-script-url:0 */

import React from 'react';
import _ from 'underscore';
import classNames from 'classnames';

const placeholderNumberIndex = 1;
const placeholderRegex = /^{([0-9]+)}$/;
const plainTextSplitter = /({[0-9]+})/g;

type ElementHelperAnchorProps = Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target'>;

export type ElementHelperInjectionType = 'link' | 'bold' | 'span' | (string & {});
export type ElementHelperClassNames = string[];
export type ElementHelperLinkTargets = Array<ElementHelperAnchorProps['target']>;

export interface ElementHelperInjectionOption extends ElementHelperAnchorProps {
  type?: ElementHelperInjectionType;
  text?: string;
  classNames?: ElementHelperClassNames;
}

type ElementHelperInjectionOptionMap = Record<string, ElementHelperInjectionOption | undefined>;

export type ElementHelperInjectionOptions = Record<string, ElementHelperInjectionOption> | ElementHelperInjectionOption[];

function renderSplitTexts(splitTexts: string[], options?: ElementHelperInjectionOptions | null | string) {
  return (
    <span>
      {_.map(splitTexts, (splitText: string) => {
        const placeholderMatcher = placeholderRegex.exec(splitText);
        if (placeholderMatcher) {
          const index = placeholderMatcher[placeholderNumberIndex];
          const option = options ? (options as ElementHelperInjectionOptionMap)[index] : undefined;
          const type = _.result(option, 'type', '');

          switch (type) {
            case 'link':
            return (
              <a
                key={index}
                href={option.href || '#'}
                className={classNames(option.classNames)}
                target={option.target}
              >
                {option.text || ''}
              </a>
            );
            case 'bold':
            return (<strong key={index} className={classNames(option.classNames)}>{option.text || ''}</strong>);
            case 'span':
            return (<span key={index} className={classNames(option.classNames)}>{option.text || ''}</span>);
            default:
            return option ? option.text : '';
          }
        }
        return splitText;
      })}
    </span>
  );
}

/*
 * renderTextWithInjections - render a combined text with injected elements including links or bolds
 * @param {string} plainText - plain text with placeholders in format '{0}...{n}'
 * @param {object} options - an array of option object to describe injections
 * @param {string} options.key - indicate the placeholder number
 * @param {string} options[key].type - indicate the type of the injection
 * @param {string} options[key].text - indicate the text to render the injection
 * @param {string} options[key].href - indicate the url of the link when type is link
 * @param {array<string>} options[i].classNames - an array of of class names to decorate the injection
 * @returns Node - a cluster of nodes to render the plain text with injected elements inside
 */
export function renderTextWithInjections(plainText: string, options?: ElementHelperInjectionOptions | null | string) {
  const splitTexts = plainText.split(plainTextSplitter);

  return renderSplitTexts(splitTexts, options);
}

/*
 * renderTextWithLinks - render a combined text with links
 * @param {string} plainText - plain text with placeholders in format '{0}...{n}'
 * @param {array<string>} linkTexts - an array of texts string to fill in plain text
 * @param {array<string>} linkUrls - an array of urls to indicate the link in each placeholder
 * @param {array<string>} customClassNames - an array of class to decorate each injection
 * @returns Node - a cluster of nodes to render the plain text with links
 */
export function renderTextWithLinks(plainText: string, linkTexts: string[], linkUrls: string[], customClassNames: ElementHelperClassNames = [], linkTargets: ElementHelperLinkTargets = []) {
  const options = _.mapObject(linkTexts, (linkText: string, index: number) => ({
    type: 'link',
    text: linkText,
    href: linkUrls[Number(index)],
    classNames: customClassNames,
    target: linkTargets[Number(index)],
  }));

  return renderTextWithInjections(plainText, options);
}

/*
 * renderTextWithBolds - render a combined text with bolds
 * @param {string} plainText - plain text with placeholders in format '{0}...{n}'
 * @param {array<string>} boldTexts - an array of texts string to fill by bolds
 * @param {array<string>} customClassNames - an array of class to decorate each injection
 * @returns Node - a cluster of nodes to render the plain text with bolds
 */
export function renderTextWithBolds(plainText: string, boldTexts: string[], customClassNames: ElementHelperClassNames = []) {
  const options = _.mapObject(boldTexts, (boldText: string) => ({
    type: 'bold',
    text: boldText,
    classNames: customClassNames,
  }));

  return renderTextWithInjections(plainText, options);
}

/*
 * renderTextWithSpans - render a combined text with spans
 * @param {string} plainText - plain text with placeholders in format '{0}...{n}'
 * @param {array<string>} spanTexts - an array of texts string to fill by spans
 * @param {array<string>} customClassNames - an array of class to decorate each injection
 * @returns Node - a cluster of nodes to render the plain text with spans
 */
export function renderTextWithSpans(plainText: string, spanTexts: string[], customClassNames: ElementHelperClassNames = []) {
  const options = _.mapObject(spanTexts, (spanText: string) => ({
    type: 'span',
    text: spanText,
    classNames: customClassNames,
  }));

  return renderTextWithInjections(plainText, options);
}
